import { format, isFuture, isValid } from "date-fns";
import type { AssistedAffiliationListFilters } from "@/services/assistedAffiliation/types";
import { LIST_DATE_FORMAT, MIN_SEARCH_TERM_LENGTH } from "./constants";
import type { AssistedAffiliationFilters } from "./types";

// Un `Date` inválido es lo que emite el calendario mientras se teclea la fecha: todavía no es un
// filtro. Una fecha futura tampoco: el calendario no deja elegirla y la marca en rojo al teclearla,
// así que consultarla dejaba la bandeja vacía con un aviso de error encima de una fecha ya señalada
// como incorrecta. Lo que la barra rechaza, no viaja.
// Y se formatea por componentes locales a propósito: el día elegido en GMT-4 convertido a UTC cae
// en el día anterior.
const toListDate = (date: Date | null): string | undefined =>
  date && isValid(date) && !isFuture(date) ? format(date, LIST_DATE_FORMAT) : undefined;

/**
 * Traduce lo que muestra la barra a lo que espera el endpoint: el término viaja bajo el nombre del
 * campo elegido y solo cuando alcanza el mínimo; las fechas, solo cuando son un día real. La
 * exportación de resultados usa esta misma traducción, así que vive aquí y no dentro de la pantalla.
 */
export const filtersToListFilters = ({
  searchKey,
  searchTerm,
  dateFrom,
  dateTo,
  status,
}: AssistedAffiliationFilters): AssistedAffiliationListFilters => {
  const listFilters: AssistedAffiliationListFilters = {};

  const term = searchTerm.trim();
  if (term.length >= MIN_SEARCH_TERM_LENGTH) listFilters[searchKey] = term;

  if (status) listFilters.status = status;

  const from = toListDate(dateFrom);
  const to = toListDate(dateTo);
  // El calendario ya marca en rojo un rango invertido; consultarlo devolvería una bandeja vacía sin
  // explicación, así que no viaja ninguna de las dos fechas hasta que el rango tenga sentido.
  // Comparar el texto basta porque `yyyy-MM-dd` ordena igual que la fecha.
  const isInvertedRange = Boolean(from && to && from > to);
  if (from && !isInvertedRange) listFilters.dateFrom = from;
  if (to && !isInvertedRange) listFilters.dateTo = to;

  return listFilters;
};

// Dos consultas son la misma si piden lo mismo, sin importar el orden en que se armaron las claves.
export const sameListFilters = (
  first: AssistedAffiliationListFilters,
  second: AssistedAffiliationListFilters,
): boolean =>
  (Object.keys({ ...first, ...second }) as (keyof AssistedAffiliationListFilters)[])
    .every((key) => first[key] === second[key]);

export const hasListFilters = (
  listFilters: AssistedAffiliationListFilters,
): boolean => Object.keys(listFilters).length > 0;
